import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Collapse,
  Input,
  Progress,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { submitEvaluation } from "../../lib/api";
import type {
  ScoreValue,
  ScoringAggregate,
  ScoringEvaluation,
  ScoringRubric,
} from "../intakeDetails/types";
import EvaluationReview from "./EvaluationReview";

interface Props {
  intakeId: string;
  rubric: ScoringRubric | null;
  aggregate: ScoringAggregate | null;
  evaluation: ScoringEvaluation | null;
  onSubmitted: () => void;
}

const SCORE_VALUES: ScoreValue[] = [1, 2, 3, 4, 5];

type ScoreEntry = { score: ScoreValue; notes?: string };

export default function ScoringChart({
  intakeId,
  rubric,
  aggregate,
  evaluation,
  onSubmitted,
}: Props) {
  const { message } = App.useApp();
  const [scores, setScores] = useState<Record<string, ScoreEntry>>({});
  const [overallNotes, setOverallNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<"edit" | "review">(
    evaluation ? "review" : "edit"
  );

  useEffect(() => {
    if (evaluation) {
      setScores((evaluation.scores as Record<string, ScoreEntry>) || {});
      setOverallNotes(evaluation.overallNotes || "");
    }
  }, [evaluation]);

  useEffect(() => {
    setMode(evaluation ? "review" : "edit");
  }, [evaluation?.id]);

  const totalQuestions = useMemo(() => {
    if (!rubric) return 0;
    return rubric.categories.reduce(
      (sum, cat) =>
        sum +
        cat.criteria.reduce(
          (criterionSum, crit) => criterionSum + (crit.questions?.length || 0),
          0
        ),
      0
    );
  }, [rubric]);

  if (!rubric) {
    return (
      <Alert
        type="info"
        showIcon
        message="No rubric loaded"
        description="Run scripts/seed_rubric.py (or POST workflowsHandler with action=seed_rubric) to populate scoringRubrics/current."
      />
    );
  }

  if (mode === "review" && evaluation) {
    return (
      <EvaluationReview
        rubric={rubric}
        evaluation={evaluation}
        aggregate={aggregate}
        onEdit={() => setMode("edit")}
      />
    );
  }

  const scoredCount = Object.keys(scores).length;

  const setScore = (questionId: string, score: ScoreValue) => {
    setScores((prev) => ({
      ...prev,
      [questionId]: { score, notes: prev[questionId]?.notes || "" },
    }));
  };

  const setNotes = (questionId: string, notes: string) => {
    setScores((prev) => {
      const existing = prev[questionId];
      if (!existing) return prev;
      return { ...prev, [questionId]: { ...existing, notes } };
    });
  };

  const handleSubmit = async () => {
    if (scoredCount === 0) {
      message.warning("Score at least one question before submitting.");
      return;
    }
    try {
      setSubmitting(true);
      await submitEvaluation({ intakeId, scores, overallNotes });
      message.success("Evaluation submitted");
      setMode("review");
      onSubmitted();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Space wrap size="large" align="center" style={{ width: "100%" }}>
          <Statistic
            title="Progress"
            value={`${scoredCount} / ${totalQuestions}`}
            valueStyle={{ fontSize: 24 }}
          />
          <div style={{ flex: 1, minWidth: 220 }}>
            <Progress
              percent={
                totalQuestions === 0
                  ? 0
                  : Math.round((scoredCount / totalQuestions) * 100)
              }
              aria-label={`${scoredCount} of ${totalQuestions} questions scored`}
            />
          </div>
          {evaluation ? (
            <Button onClick={() => setMode("review")}>Cancel edits</Button>
          ) : null}
        </Space>
      </Card>

      <Collapse
        defaultActiveKey={rubric.categories.map((c) => c.id)}
        items={rubric.categories.map((category) => ({
          key: category.id,
          label: (
            <Typography.Text strong>{category.name}</Typography.Text>
          ),
          children: (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {category.criteria.map((criterion) => (
                <Card
                  key={criterion.id}
                  size="small"
                  title={
                    <Typography.Text strong>{criterion.name}</Typography.Text>
                  }
                >
                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: "100%" }}
                  >
                    {(criterion.questions || []).map((question) => {
                      const current = scores[question.id];
                      const groupName = `score-${question.id}`;
                      return (
                        <div key={question.id}>
                          <Typography.Paragraph
                            id={`${question.id}-label`}
                            style={{ marginBottom: 8 }}
                          >
                            {question.text}
                          </Typography.Paragraph>
                          <fieldset
                            aria-labelledby={`${question.id}-label`}
                            style={{
                              border: "none",
                              padding: 0,
                              margin: 0,
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <legend
                              style={{
                                position: "absolute",
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: "hidden",
                                clip: "rect(0,0,0,0)",
                                whiteSpace: "nowrap",
                                border: 0,
                              }}
                            >
                              Score for {question.text}
                            </legend>
                            {SCORE_VALUES.map((value) => {
                              const checked = current?.score === value;
                              const inputId = `${question.id}-${value}`;
                              const anchor =
                                value === 1 || value === 3 || value === 5
                                  ? question.anchors[String(value) as "1" | "3" | "5"]
                                  : undefined;
                              const ariaLabel = anchor
                                ? `Score ${value} of 5 — ${anchor}`
                                : `Score ${value} of 5`;
                              return (
                                <label
                                  key={value}
                                  htmlFor={inputId}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    border: checked
                                      ? "2px solid currentColor"
                                      : "1px solid #d9d9d9",
                                    borderRadius: 6,
                                    padding: "4px 12px",
                                    cursor: "pointer",
                                    minWidth: 40,
                                    justifyContent: "center",
                                  }}
                                >
                                  <input
                                    id={inputId}
                                    type="radio"
                                    name={groupName}
                                    value={value}
                                    checked={checked}
                                    onChange={() => setScore(question.id, value)}
                                    aria-label={ariaLabel}
                                  />
                                  <span>{value}</span>
                                </label>
                              );
                            })}
                          </fieldset>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              marginTop: 12,
                            }}
                          >
                            {(["1", "3", "5"] as const).map((key) => (
                              <Tag
                                key={key}
                                color="default"
                                style={{
                                  whiteSpace: "normal",
                                  margin: 0,
                                  padding: "4px 10px",
                                  lineHeight: 1.4,
                                }}
                              >
                                <strong style={{ marginRight: 6 }}>{key}</strong>
                                {question.anchors[key]}
                              </Tag>
                            ))}
                          </div>
                          {question.helpText ? (
                            <Typography.Paragraph
                              type="secondary"
                              italic
                              style={{ marginTop: 8, marginBottom: 4 }}
                            >
                              {question.helpText}
                            </Typography.Paragraph>
                          ) : null}
                          {current ? (
                            <Input.TextArea
                              value={current.notes || ""}
                              onChange={(event) =>
                                setNotes(question.id, event.target.value)
                              }
                              placeholder="Evaluator notes (optional)"
                              autoSize={{ minRows: 2 }}
                              aria-label={`Notes for ${question.text}`}
                              style={{ marginTop: 8 }}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </Space>
                </Card>
              ))}
            </Space>
          ),
        }))}
      />

      <Card title="Overall evaluator notes">
        <Input.TextArea
          value={overallNotes}
          onChange={(event) => setOverallNotes(event.target.value)}
          placeholder="Summarize your overall recommendation (optional)"
          autoSize={{ minRows: 3 }}
          aria-label="Overall evaluator notes"
        />
        <div style={{ marginTop: 12 }}>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            {evaluation ? "Update evaluation" : "Submit evaluation"}
          </Button>
        </div>
      </Card>
    </Space>
  );
}
